/**
 * bluetoothRecovery.js
 *
 * Android-only helper that power-cycles the Bluetooth adapter when the
 * cashier / Bluetooth printer stops responding. Used by the
 * `CashRegisterRetryDialog` after several consecutive failures.
 *
 * On modern Android (API 33+) the OS rejects programmatic
 * BluetoothAdapter.disable()/enable() calls from non-system apps. In that
 * case the helper degrades gracefully:
 *   - `disableBluetooth()` / `enableBluetooth()` resolve `{ ok:false, reason:'permission' }`
 *     so the UI can ask the operator to toggle Bluetooth manually while
 *     a 10-second countdown runs.
 *
 * The public API is intentionally tiny so it can be used from a state
 * machine in the dialog without pulling in extra dependencies:
 *
 *   import {
 *     isBluetoothToggleSupported,
 *     disableBluetooth,
 *     enableBluetooth,
 *     waitForAdapterEnabled,
 *     powerCycleBluetooth,
 *   } from './bluetoothRecovery';
 */

import {Platform} from 'react-native';

const ADAPTER_POLL_INTERVAL_MS = 500;
const DEFAULT_WAIT_TIMEOUT_MS = 15000;

let _BluetoothClassic = null;

const getBluetoothClassic = () => {
  if (Platform.OS !== 'android') {
    return null;
  }

  if (_BluetoothClassic !== null) {
    return _BluetoothClassic;
  }

  try {
    // Lazy-require so non-Android platforms (web, iOS) do not pay the import cost.
    _BluetoothClassic = require('react-native-bluetooth-classic').default;
  } catch (_error) {
    _BluetoothClassic = false;
  }

  return _BluetoothClassic || null;
};

/**
 * Whether programmatic Bluetooth toggling is even theoretically available
 * on this device/build. The OS may still reject the action at runtime.
 */
export const isBluetoothToggleSupported = () => {
  const bt = getBluetoothClassic();
  if (!bt) {
    return false;
  }
  return typeof bt.requestBluetoothEnabled === 'function'
    || typeof bt.enableBluetooth === 'function'
    || typeof bt.disableBluetooth === 'function';
};

/**
 * Returns the current adapter state as best we can tell.
 * `true` = enabled, `false` = disabled, `null` = unknown / unsupported.
 */
export const isBluetoothEnabled = async () => {
  const bt = getBluetoothClassic();
  if (!bt || typeof bt.isBluetoothEnabled !== 'function') {
    return null;
  }

  try {
    const enabled = await bt.isBluetoothEnabled();
    return Boolean(enabled);
  } catch (_error) {
    return null;
  }
};

const sleep = (ms) => new Promise((resolve) => globalThis.setTimeout(resolve, ms));

/**
 * Wait until the adapter reaches `targetEnabled`. Returns `true` on
 * success, `false` on timeout. Polls every 500 ms.
 */
export const waitForAdapterEnabled = async (targetEnabled, timeoutMs = DEFAULT_WAIT_TIMEOUT_MS) => {
  const deadline = Date.now() + Math.max(1000, timeoutMs);

  while (Date.now() < deadline) {
    const state = await isBluetoothEnabled();
    if (state === Boolean(targetEnabled)) {
      return true;
    }
    if (state === null) {
      // Cannot read state – treat as best effort and stop early after one tick.
      return false;
    }
    await sleep(ADAPTER_POLL_INTERVAL_MS);
  }

  return false;
};

/**
 * Try to disable the Bluetooth adapter.
 * Resolves `{ ok, reason? }` and never throws.
 */
export const disableBluetooth = async () => {
  const bt = getBluetoothClassic();
  if (!bt) {
    return {ok: false, reason: 'unsupported'};
  }

  if (typeof bt.disableBluetooth !== 'function') {
    return {ok: false, reason: 'unsupported'};
  }

  try {
    await bt.disableBluetooth();
    const reached = await waitForAdapterEnabled(false, 8000);
    return reached ? {ok: true} : {ok: false, reason: 'timeout'};
  } catch (error) {
    const message = String(error?.message || error || '').toLowerCase();
    if (message.includes('permission') || message.includes('denied')) {
      return {ok: false, reason: 'permission'};
    }
    return {ok: false, reason: 'error', error};
  }
};

/**
 * Try to enable the Bluetooth adapter.
 * Resolves `{ ok, reason? }` and never throws.
 */
export const enableBluetooth = async () => {
  const bt = getBluetoothClassic();
  if (!bt) {
    return {ok: false, reason: 'unsupported'};
  }

  try {
    if (typeof bt.requestBluetoothEnabled === 'function') {
      // This shows the OS prompt on modern Android and is the recommended path.
      await bt.requestBluetoothEnabled();
    } else if (typeof bt.enableBluetooth === 'function') {
      await bt.enableBluetooth();
    } else {
      return {ok: false, reason: 'unsupported'};
    }

    const reached = await waitForAdapterEnabled(true, 12000);
    return reached ? {ok: true} : {ok: false, reason: 'timeout'};
  } catch (error) {
    const message = String(error?.message || error || '').toLowerCase();
    if (message.includes('permission') || message.includes('denied') || message.includes('cancel')) {
      return {ok: false, reason: 'permission'};
    }
    return {ok: false, reason: 'error', error};
  }
};

/**
 * Disable → wait `offDelayMs` → enable. Caller can pass a progress
 * callback that is invoked as the recovery moves through phases:
 *   onProgress({ phase: 'disabling' | 'waiting' | 'enabling' | 'done', remainingMs })
 *
 * If the OS denies programmatic toggling, this still produces a useful
 * countdown so the UI can show "Please turn Bluetooth off, then on" and
 * re-attempt the send when the adapter comes back.
 */
export const powerCycleBluetooth = async ({
  offDelayMs = 10000,
  onProgress,
} = {}) => {
  const emit = (phase, remainingMs = 0) => {
    if (typeof onProgress === 'function') {
      try {
        onProgress({phase, remainingMs});
      } catch (_error) { /* ignore */ }
    }
  };

  emit('disabling', offDelayMs);
  const offResult = await disableBluetooth();

  // Even if programmatic disable failed, run the countdown so the user
  // can do it manually – the dialog will surface the manual instruction.
  const totalSteps = Math.ceil(offDelayMs / 1000);
  for (let step = totalSteps; step > 0; step -= 1) {
    emit('waiting', step * 1000);
    await sleep(1000);
  }

  emit('enabling', 0);
  const onResult = await enableBluetooth();

  emit('done', 0);

  return {
    disableResult: offResult,
    enableResult: onResult,
    ok: onResult.ok === true,
    requiresManualToggle:
      offResult.reason === 'permission'
      || offResult.reason === 'unsupported'
      || onResult.reason === 'permission'
      || onResult.reason === 'unsupported',
  };
};

export default {
  isBluetoothToggleSupported,
  isBluetoothEnabled,
  waitForAdapterEnabled,
  disableBluetooth,
  enableBluetooth,
  powerCycleBluetooth,
};
